import { request, unwrap } from '../client';
import type {
  ApiResponse,
  CreateHostnameRequest,
  HostnameBackfillSummary,
  HostnameDto,
  RoutingDeployOutcome,
  WithRoutingDeploy,
} from '../types';

/**
 * DNS-check contract (saas-auth PR #136, ship-time confirmed / LOCKED).
 * `GET /api/saas-admin/hostnames/dns-check?hostname=<bare>[&https=true]` — the
 * shape below lives under the `ApiResponse` envelope's `.data`. `httpsOk` /
 * `httpsReason` are present only when `&https=true`. An unresolvable hostname is
 * NOT an error: 200 with `resolvedIps: []`, `match: false`.
 */
export interface DnsCheckResult {
  hostname: string;
  expectedTargets: string[];
  expectedIps: string[];
  resolvedIps: string[];
  match: boolean;
  configured: boolean;
  checkedAt: string;
  // Spec 0033 — diagnostics (feature-detected). `resolutionError` is present only when
  // the DNS query itself failed (REFUSED/timeout/…) — a verification-system problem,
  // NOT "not propagated yet". `expectedResolved === false` means OUR expected target
  // couldn't resolve — a platform-side problem. Both absent on today's backend.
  resolutionError?: string | null;
  expectedResolved?: boolean;
  httpsOk?: boolean;
  httpsReason?: string | null;
}

/**
 * Public branding-by-hostname response (go-live proof). Returned FLAT (no
 * `ApiResponse` envelope) — matches saas_login's `fetchBrand`. Only the fields
 * the wizard surfaces as proof are typed here.
 */
export interface BrandingResolution {
  displayName?: string;
  logoUrl?: string | null;
}

/**
 * verify-dns response (saas-auth 0027, LOCKED). The `ApiResponse` envelope's
 * `.data` carries `check` (the familiar dns-check fields) and `hostname` (the
 * updated row — `dnsVerified:true` once `check.match`).
 */
export interface VerifyDnsResult {
  check: DnsCheckResult;
  hostname: HostnameDto;
  /**
   * SASADM-0172 (SASATH-0388 §3C.1) — present only when this check flipped an APP row from
   * unverified to verified (and so deployed); absent on a re-check, a non-APP row or a mismatch.
   */
  routingDeploy?: RoutingDeployOutcome;
}

export const hostnamesApi = {
  async list(tenantId: number): Promise<HostnameDto[]> {
    const res = await request<ApiResponse<HostnameDto[]>>(
      'GET',
      `/api/saas-admin/tenants/${tenantId}/hostnames`,
    );
    return unwrap(res);
  },

  async create(tenantId: number, req: CreateHostnameRequest): Promise<HostnameDto> {
    const res = await request<ApiResponse<HostnameDto>>(
      'POST',
      `/api/saas-admin/tenants/${tenantId}/hostnames`,
      { json: req },
    );
    return unwrap(res);
  },

  // SASADM-0170 (SASATH-0387 §3.1) — a deploying DELETE: returns the routing deploy outcome
  // from `data.routingDeploy`; a pre-0387 204/null body → null.
  async delete(tenantId: number, hostnameId: number): Promise<RoutingDeployOutcome | null> {
    const res = await request<ApiResponse<WithRoutingDeploy | null> | undefined>(
      'DELETE',
      `/api/saas-admin/tenants/${tenantId}/hostnames/${hostnameId}`,
    );
    return res?.data?.routingDeploy ?? null;
  },

  // Spec 0029 — run the DNS check for a registered row and persist on success
  // (idempotent). Returns the check detail + the updated row.
  async verifyDns(tenantId: number, hostnameId: number): Promise<VerifyDnsResult> {
    const res = await request<ApiResponse<VerifyDnsResult>>(
      'POST',
      `/api/saas-admin/tenants/${tenantId}/hostnames/${hostnameId}/verify-dns`,
    );
    return unwrap(res);
  },

  // Spec 0032 — assign an APP hostname's app (the app must belong to the tenant's
  // product). Returns the updated row. Client-side unassign was removed (SASADM-0092/
  // 0094): this method no longer sends appId:null. The backend still accepts null on
  // this endpoint for legacy paths, but saas-admin does not use it.
  async setApp(tenantId: number, hostnameId: number, appId: number): Promise<HostnameDto> {
    const res = await request<ApiResponse<HostnameDto>>(
      'PUT',
      `/api/saas-admin/tenants/${tenantId}/hostnames/${hostnameId}/app`,
      { json: { appId } },
    );
    return unwrap(res);
  },

  // SASADM-0173 (SASATH-0390 §4) — point an APP row at another live APP row of the tenant (308),
  // or clear it with null. Returns the updated row with `routingDeploy`.
  async setRedirect(
    tenantId: number,
    hostnameId: number,
    targetHostnameId: number | null,
  ): Promise<HostnameDto> {
    const res = await request<ApiResponse<HostnameDto>>(
      'PUT',
      `/api/saas-admin/tenants/${tenantId}/hostnames/${hostnameId}/redirect`,
      { json: { targetHostnameId } },
    );
    return unwrap(res);
  },

  // Spec 0026 — DNS verification (+ optional HTTPS cert check). Enveloped → unwrap().
  async dnsCheck(hostname: string, https = false): Promise<DnsCheckResult> {
    const query: Record<string, string> = { hostname };
    if (https) query.https = 'true';
    const res = await request<ApiResponse<DnsCheckResult>>(
      'GET',
      '/api/saas-admin/hostnames/dns-check',
      { query },
    );
    return unwrap(res);
  },

  // Spec 0026 — go-live proof: resolve the public brand for an APP host. Public
  // endpoint (auth base, no bearer), returned FLAT (no envelope). Throws ApiError
  // (e.g. BRAND_NOT_CONFIGURED) when the host has no brand yet.
  async resolveBranding(host: string): Promise<BrandingResolution> {
    return request<BrandingResolution>('GET', '/api/branding/by-hostname', {
      query: { host },
    });
  },

  // SASADM-0118 (saas-auth SASATH-0186 / SASATH-0247) — PLATFORM-WIDE default-hostname
  // backfill: provisions missing LOGIN/AUTH/BRAND/ASSET rows for every tenant. No body.
  // Both query params are optional; `buildUrl` drops `undefined`, so an absent cursor
  // sends no param and the server applies its own default limit (200, clamped 1–500).
  // A `truncated` response means the walk stopped at `nextTenantId` — the caller must
  // resume, never report success.
  async backfillDefaultHostnames(params?: {
    afterTenantId?: number;
    limit?: number;
  }): Promise<HostnameBackfillSummary> {
    const res = await request<ApiResponse<HostnameBackfillSummary>>(
      'POST',
      '/api/saas-admin/hostnames/backfill-defaults',
      { query: { afterTenantId: params?.afterTenantId, limit: params?.limit } },
    );
    return unwrap(res);
  },
};
